import { videoWatermarkPreset } from './videoWatermarks.ts';
import { videoBookendPreset } from './videoBookends.ts';

export const VIDEO_TRANSITIONS = [
  { value: 'none', label: '直接切换' },
  { value: 'fade', label: '淡入淡出' },
  { value: 'dissolve', label: '叠化' },
  { value: 'wipeleft', label: '向左擦除' },
  { value: 'slideright', label: '向右滑动' },
  { value: 'fadeblack', label: '黑场过渡' },
] as const;

export type VideoTransition = typeof VIDEO_TRANSITIONS[number]['value'];
export interface VideoFrameSettings {
  image_url: string;
  fit: 'contain' | 'cover';
  zoom: number;
  position_x: number;
  position_y: number;
  transition: VideoTransition | 'inherit';
  transition_seconds: number;
}
export interface VideoBookend extends VideoFrameSettings { enabled: boolean; duration_seconds: number; preset_id?: string }
export interface VideoClipSettings extends VideoFrameSettings { clip_id: string }
export interface VideoAudioTrack {
  id: string; name: string; url: string; source_duration: number;
  start_seconds: number; trim_start_seconds: number; duration_seconds: number;
  volume: number; muted: boolean;
}
export const WATERMARK_POSITIONS = [
  { value: 'top-left', label: '左上' }, { value: 'top-right', label: '右上' },
  { value: 'bottom-left', label: '左下' }, { value: 'bottom-right', label: '右下' },
  { value: 'center', label: '居中' },
] as const;
export interface VideoWatermark {
  enabled: boolean; kind: 'preset' | 'text' | 'image'; preset_id: string; text: string; asset_url: string;
  position: typeof WATERMARK_POSITIONS[number]['value'];
  size_percent: number; opacity: number; margin_px: number;
}
export interface VideoCompositionSettings {
  transition: VideoTransition;
  transition_seconds: number;
  split_subtitles: boolean;
  subtitle_max_chars: number;
  opening: VideoBookend;
  ending: VideoBookend;
  clips: VideoClipSettings[];
  audio_tracks: VideoAudioTrack[];
  watermark: VideoWatermark;
}
function object(value: unknown, label: string): Record<string, unknown> {
  if (value === undefined) return {};
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(label + '格式无效');
  return value as Record<string, unknown>;
}
function number(value: unknown, fallback: number, min: number, max: number, label: string) {
  const result = value ?? fallback;
  if (typeof result !== 'number' || !Number.isFinite(result) || result < min || result > max) throw new Error(label + '超出有效范围');
  return result;
}
function boolean(value: unknown, fallback: boolean, label: string) {
  if (value === undefined) return fallback;
  if (typeof value !== 'boolean') throw new Error(label + '无效');
  return value;
}
function text(value: unknown, fallback: string, max: number, label: string) {
  if (value === undefined) return fallback;
  if (typeof value !== 'string' || value.length > max || /[\u0000-\u001f]/u.test(value)) throw new Error(label + '无效');
  return value;
}
function tenth(value: unknown, fallback: number, min: number, max: number, label: string) {
  const result = number(value, fallback, min, max, label);
  if (Math.abs(result * 10 - Math.round(result * 10)) > 0.00001) throw new Error(label + '须以 0.1 秒为单位');
  return Math.round(result * 10) / 10;
}
export function videoFrameSettings(value?: unknown): VideoFrameSettings {
  const input = object(value, '关键帧设置');
  const transition = input.transition ?? 'inherit';
  if (transition !== 'inherit' && !VIDEO_TRANSITIONS.some(option => option.value === transition)) throw new Error('换场特效无效');
  const fit = input.fit ?? 'contain';
  if (fit !== 'contain' && fit !== 'cover') throw new Error('画面适配方式无效');
  return { image_url: text(input.image_url, '', 1024, '关键帧路径'), fit,
    zoom: number(input.zoom, 1, 1, 3, '画面缩放'),
    position_x: number(input.position_x, 50, 0, 100, '水平位置'),
    position_y: number(input.position_y, 50, 0, 100, '垂直位置'),
    transition: transition as VideoFrameSettings['transition'],
    transition_seconds: number(input.transition_seconds, 0.6, 0.2, 1.2, '换场时长') };
}
function bookend(value: unknown, kind: 'opening' | 'ending'): VideoBookend {
  const input = object(value, '片头片尾设置');
  const preset = videoBookendPreset(text(input.preset_id, '', 128, '预制视频'));
  return { ...videoFrameSettings(input), enabled: boolean(input.enabled, false, '片头片尾开关'),
    duration_seconds: preset?.duration_seconds[kind] ?? tenth(input.duration_seconds, 3, 0.1, 600, '片头片尾时长'),
    ...(preset ? { preset_id: preset.id } : {}) };
}
function list(value: unknown, max: number, label: string): unknown[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > max) throw new Error(label + '格式或数量无效');
  return value;
}
export function videoWatermarkSettings(value?: unknown): VideoWatermark {
  const input = object(value, '正片水印');
  const kind = input.kind ?? 'preset';
  if (kind !== 'preset' && kind !== 'text' && kind !== 'image') throw new Error('水印类型无效');
  const position = input.position ?? 'top-right';
  if (!WATERMARK_POSITIONS.some(option => option.value === position)) throw new Error('水印位置无效');
  const enabled = boolean(input.enabled, false, '水印开关');
  const content = text(input.text, 'IVS', 80, '水印文字');
  if (enabled && kind === 'text' && !content.trim()) throw new Error('请填写水印文字');
  const preset_id = text(input.preset_id, 'haochen-pearl-v2', 128, '频道水印样式');
  videoWatermarkPreset(preset_id);
  return { enabled, kind, preset_id, text: content, asset_url: text(input.asset_url, '', 1024, '水印图片'),
    position: position as VideoWatermark['position'], size_percent: number(input.size_percent, 7.5, 5, 50, '水印大小'),
    opacity: number(input.opacity, 0.65, 0.05, 1, '水印透明度'), margin_px: number(input.margin_px, 32, 0, 192, '水印边距') };
}
export function videoCompositionSettings(value?: unknown): VideoCompositionSettings {
  const input = object(value, '视频合成设置');
  const transition = input.transition ?? 'none';
  if (!VIDEO_TRANSITIONS.some(option => option.value === transition)) throw new Error('换场特效无效');
  const subtitleMax = number(input.subtitle_max_chars, 24, 12, 48, '每条字幕字数');
  if (!Number.isInteger(subtitleMax)) throw new Error('每条字幕字数须为整数');
  const clips = list(input.clips, 10000, '片段设置').map(value => {
    const item = object(value, '片段设置');
    const clip_id = text(item.clip_id, '', 512, '片段 ID');
    if (!clip_id.startsWith('shot:') || clip_id.length < 8) throw new Error('片段 ID 无效');
    return { ...videoFrameSettings(item), clip_id };
  });
  if (new Set(clips.map(item => item.clip_id)).size !== clips.length) throw new Error('片段设置重复');
  const audio_tracks = list(input.audio_tracks, 16, '附加音轨').map(value => {
    const item = object(value, '附加音轨');
    const id = text(item.id, '', 128, '音轨 ID');
    if (!/^[a-zA-Z0-9_-]+$/.test(id)) throw new Error('音轨 ID 无效');
    const source_duration = number(item.source_duration, 0, 0.001, 14400, '音频源时长');
    const trim_start_seconds = tenth(item.trim_start_seconds, 0, 0, 14400, '音频入点');
    const duration_seconds = tenth(item.duration_seconds, Math.floor(source_duration * 10) / 10, 0.1, 14400, '音轨时长');
    if (trim_start_seconds + duration_seconds > source_duration + 0.001) throw new Error('音轨裁剪范围超过素材时长');
    return { id, name: text(item.name, '附加音轨', 255, '音轨名称'), url: text(item.url, '', 1024, '音轨路径'), source_duration,
      start_seconds: tenth(item.start_seconds, 0, 0, 86400, '音轨位置'), trim_start_seconds, duration_seconds,
      volume: number(item.volume, 1, 0, 1, '音轨音量'), muted: boolean(item.muted, false, '音轨静音') };
  });
  if (new Set(audio_tracks.map(item => item.id)).size !== audio_tracks.length) throw new Error('音轨 ID 重复');
  return { transition: transition as VideoTransition,
    transition_seconds: number(input.transition_seconds, 0.6, 0.2, 1.2, '换场时长'),
    split_subtitles: boolean(input.split_subtitles, true, '字幕自动拆分设置'), subtitle_max_chars: subtitleMax,
    opening: bookend(input.opening, 'opening'), ending: bookend(input.ending, 'ending'), clips, audio_tracks, watermark: videoWatermarkSettings(input.watermark) };
}
